import type { Lesson } from './types'

export const laamRulesLessons: Lesson[] = [
  {
    id: 'laam-jalalah',
    title: 'Laam of Allah\'s Name (تفخيم / ترقيق)',
    summary: 'The laam in "Allah" is heavy after fatha/damma, light after kasra.',
    explanation: [
      'The laam in the word اللَّه (Allah) changes weight depending on what comes right before it - this is one of the few letters in Arabic whose pronunciation shifts based on context rather than always being heavy or always being light.',
      'Tafkhim (heavy/thick) - when the word before ends in a fatha or damma, the laam is pronounced heavy, with the back of the tongue raised toward the soft palate, giving it a fuller, rounder sound.',
      'Tarqiq (light/thin) - when the word before ends in a kasra, the laam is pronounced light and thin, with no raising of the tongue.',
    ],
    colorLegend: [
      { color: 'heavy', label: 'Heavy laam (after fatha/damma)' },
      { color: 'light', label: 'Light laam (after kasra)' },
    ],
    examples: [
      { arabic: 'قَالَ اللَّهُ', transliteration: 'qālallāhu', meaning: 'Allah said', highlights: [{ text: 'لَّ', color: 'heavy' }] },
      { arabic: 'نَصْرُ اللَّهِ', transliteration: 'naṣrullāh', meaning: 'the help of Allah', highlights: [{ text: 'لَّ', color: 'heavy' }] },
      { arabic: 'بِسْمِ اللَّهِ', transliteration: 'bismillāh', meaning: 'in the name of Allah', highlights: [{ text: 'لَّ', color: 'light' }] },
    ],
  },
  {
    id: 'laam-taarif',
    title: 'Laam Ta\'rif: Sun Letters and Moon Letters',
    summary: 'The laam of "the" (ال) disappears before sun letters and stays before moon letters.',
    explanation: [
      'Laam Ta\'rif is the laam in ال ("the") at the start of a word. What happens to it depends entirely on which letter follows it - and every one of the 28 letters falls into exactly one of two groups.',
      'Sun letters (idgham shamsiyyah) - ت ث د ذ ر ز س ش ص ض ط ظ ل ن. The laam is not pronounced at all; it fully assimilates into the following letter, which is then pronounced with a shaddah (doubled). Written in the Mushaf, the laam appears with no vowel mark and no sukun - just silent.',
      'Moon letters (izhar qamariyyah) - ا ب ج ح خ ع غ ف ق ك م ه و ي. The laam is pronounced clearly with a sukun, exactly as written.',
      'The names come from the two example words traditionally used to teach this: الشَّمْس (ash-shams, "the sun" - a sun letter, laam silent) and الْقَمَر (al-qamar, "the moon" - a moon letter, laam pronounced).',
    ],
    colorLegend: [
      { color: 'idgham', label: 'Sun letter - laam silent, next letter doubled' },
      { color: 'izhar', label: 'Moon letter - laam pronounced clearly' },
    ],
    examples: [
      { arabic: 'الشَّمْسُ', transliteration: 'ash-shamsu', meaning: 'the sun', highlights: [{ text: 'شَّ', color: 'idgham' }] },
      { arabic: 'الرَّحْمَٰنِ', transliteration: 'ar-raḥmān', meaning: 'the Most Merciful', highlights: [{ text: 'رَّ', color: 'idgham' }] },
      { arabic: 'النَّاسِ', transliteration: 'an-nās', meaning: 'mankind', highlights: [{ text: 'نَّ', color: 'idgham' }] },
      { arabic: 'الْقَمَرُ', transliteration: 'al-qamaru', meaning: 'the moon', highlights: [{ text: 'لْ', color: 'izhar' }] },
      { arabic: 'الْحَمْدُ', transliteration: 'al-ḥamdu', meaning: 'the praise', highlights: [{ text: 'لْ', color: 'izhar' }] },
      { arabic: 'الْكِتَابُ', transliteration: 'al-kitābu', meaning: 'the book', highlights: [{ text: 'لْ', color: 'izhar' }] },
    ],
  },
]
